import Pagination from 'components/Blocks/Pagination/Pagination';
import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import { categoryDetailContentMessageTwClass } from 'components/Pages/CategoryDetail/CategoryDetailProductsWrapper';
import { mapListedProductConnectionType } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { FlagProductsQueryDocumentApi } from 'graphql/generated';
import { useProductsData } from 'helpers/loadMore/useProductsData';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useGtmFlagProductListView } from 'hooks/gtm/useGtmFlagProductListView';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { RefObject, useEffect, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { FlagDetailType } from 'types/flag';

type FlagDetailProductsWrapperProps = {
    flag: FlagDetailType;
    paginationScrollTargetRef: RefObject<HTMLDivElement>;
};

export const FlagDetailProductsWrapper: FC<FlagDetailProductsWrapperProps> = ({ flag, paginationScrollTargetRef }) => {
    const { t } = useTranslation();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const { asPath } = useRouter();
    const currentPage = useCurrentPageQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const { currencyCode } = useDomainConfig();
    const totalCount = flag.productConnection.totalCount;
    const authLoading = usePersistStore((s) => s.authLoading);
    const setWasRedirectedToSeoCategory = useSessionStore((s) => s.setWasRedirectedToSeoCategory);

    const { products, areProductsFetching, isLoadingMoreProducts } = useProductsData(
        FlagProductsQueryDocumentApi,
        totalCount,
        shouldRefetch,
        {
            pause: authLoading !== 'not-loading',
            shouldAbortFetchingProducts: false,
            abortedFetchCallback: () => setWasRedirectedToSeoCategory(false),
        },
    );
    const flagProducts = products ? mapListedProductConnectionType(products, currencyCode).products : [];

    useGtmFlagProductListView(flag, getUrlWithoutGetParameters(asPath), flagProducts, areProductsFetching);

    useBroadcastChannel('orderCompleteBroadcast', () => {
        setShouldRefetch(true);
    });

    useEffect(() => {
        if (shouldRefetch) {
            setShouldRefetch(false);
        }
    }, [shouldRefetch]);

    return (
        <>
            {(flagProducts.length !== 0 || areProductsFetching) && (
                <ProductsList
                    currentPage={currentPage}
                    gtmListName="flag"
                    isLoadMore={currentLoadMore > 0}
                    isLoading={areProductsFetching || isLoadingMoreProducts}
                    products={flagProducts}
                    type={ProductListTypes.Normal}
                />
            )}
            {flagProducts.length !== 0 && (
                <Pagination
                    isWithLoadMore
                    isLoading={isLoadingMoreProducts}
                    paginationScrollTargetRef={paginationScrollTargetRef}
                    totalCount={totalCount}
                />
            )}
            {totalCount === 0 && !areProductsFetching && (
                <div className={categoryDetailContentMessageTwClass}>
                    <div>
                        <strong>{t('No results match the filter')}</strong>
                    </div>
                    <div>
                        <Trans components={{ 0: <br /> }} i18nKey="ProductsNoResults" />
                    </div>
                </div>
            )}
        </>
    );
};
